const SOCIAL_FIELD_BY_TYPE = {
  linkedin: "linkedin_url",
  facebook: "facebook_url",
  twitter: "twitter_url",
  google: "google_url",
  instagram: "instagram_url",
  youtube: "youtube_url",
  tiktok: "tiktok_url",
};

export const COMPANY_SOCIAL_TYPES = Object.keys(SOCIAL_FIELD_BY_TYPE);

function safeTrim(value) {
  return value == null ? "" : String(value).trim();
}

function safeJsonParse(value) {
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function normalizeExternalUrl(value = "") {
  const raw = safeTrim(value);
  if (!raw) return "";

  try {
    const normalized = /^https?:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/+/, "")}`;
    const parsed = new URL(normalized);
    if (!["http:", "https:"].includes(parsed.protocol)) return "";
    return parsed.toString();
  } catch {
    return "";
  }
}

function canonicalizeUrlForDedup(value = "") {
  const normalized = normalizeExternalUrl(value);
  if (!normalized) return safeTrim(value).toLowerCase();

  try {
    const parsed = new URL(normalized);
    const host = parsed.hostname.replace(/^www\./i, "").toLowerCase();
    const pathname = parsed.pathname.replace(/\/+$/, "") || "/";
    const search = parsed.search || "";
    return `${parsed.protocol}//${host}${pathname}${search}`;
  } catch {
    return normalized.toLowerCase();
  }
}

function collectCompanyUrlCandidates(company = {}) {
  return [
    company?.website,
    company?.company_website,
    company?.website_url,
    company?.company_link,
    company?.career_site,
    company?.career_website,
    company?.company_career_website,
    company?.raw?.website,
    company?.raw?.company_website,
    company?.raw?.website_url,
    company?.raw?.company_link,
    company?.raw?.career_site,
    company?.raw?.career_website,
    company?.raw?.company_career_website,
  ];
}

function normalizeRepeatedLinks(raw = [], options = {}) {
  const preserveEmpty = options.preserveEmpty === true;
  const source = Array.isArray(raw)
    ? raw
    : typeof raw === "string"
    ? (() => {
        const parsed = safeJsonParse(raw.trim());
        if (Array.isArray(parsed)) return parsed;
        return raw.split(",").map((item) => item.trim());
      })()
    : [];

  const links = [];
  const seen = new Set();

  for (const item of source) {
    const value = String(item ?? "");
    const trimmed = value.trim();

    if (!trimmed) {
      if (preserveEmpty) links.push("");
      continue;
    }

    const normalized = normalizeExternalUrl(trimmed) || trimmed;
    const key = canonicalizeUrlForDedup(normalized);
    if (seen.has(key)) continue;
    seen.add(key);
    links.push(normalized);
  }

  return links;
}

export function normalizeCompanySocialLinksArray(value = [], options = {}) {
  return normalizeRepeatedLinks(value, options);
}

export function detectCompanySocialType(url = "", fallbackType = "website") {
  const normalizedUrl = normalizeExternalUrl(url);
  if (!normalizedUrl) return fallbackType;

  try {
    const parsedUrl = new URL(normalizedUrl);
    const host = parsedUrl.hostname.replace(/^www\./i, "").toLowerCase();
    const path = parsedUrl.pathname.toLowerCase();

    // Exclude LinkedIn job URLs from social media detection
    if (host.includes("linkedin.") && path.startsWith("/jobs/view/")) {
      return fallbackType;
    }

    if (host.includes("linkedin.")) return "linkedin";
    if (host.includes("facebook.")) return "facebook";
    if (host === "x.com" || host.endsWith(".x.com") || host.includes("twitter.")) return "twitter";
    if (
      host === "g.page" ||
      host.endsWith(".g.page") ||
      host.startsWith("plus.google.") ||
      host.startsWith("business.google.") ||
      (host.includes("google.") &&
        (path.startsWith("/maps") ||
          path.startsWith("/search") ||
          path.startsWith("/local") ||
          path.startsWith("/travel")))
    ) {
      return "google";
    }
    if (host.includes("instagram.")) return "instagram";
    if (host.includes("youtube.") || host === "youtu.be" || host.endsWith(".youtu.be")) return "youtube";
    if (host.includes("tiktok.")) return "tiktok";
  } catch {
    return fallbackType;
  }

  return fallbackType;
}

export function isCompanySocialUrl(url = "") {
  return COMPANY_SOCIAL_TYPES.includes(detectCompanySocialType(url, ""));
}

export function getPrimaryCompanyWebsite(company = {}) {
  return (
    collectCompanyUrlCandidates(company)
      .map((value) => normalizeExternalUrl(value))
      .find((value) => value && !isCompanySocialUrl(value)) || ""
  );
}

export function getCompanySocialLinksArray(company = {}, options = {}) {
  const explicit =
    company?.social_links ??
    company?.socialLinks ??
    company?.social_links_text ??
    company?.socialLinksText ??
    company?.raw?.social_links ??
    company?.raw?.socialLinks ??
    company?.raw?.social_links_text ??
    company?.raw?.socialLinksText;

  if (
    explicit !== undefined &&
    explicit !== null &&
    (!(typeof explicit === "string") || explicit.trim() !== "")
  ) {
    const normalizedExplicit = normalizeRepeatedLinks(explicit, options);
    if (normalizedExplicit.length || options.preserveEmpty === true) {
      return normalizedExplicit;
    }
  }

  const candidates = [
    ...COMPANY_SOCIAL_TYPES.flatMap((type) => [
      company?.[SOCIAL_FIELD_BY_TYPE[type]],
      company?.raw?.[SOCIAL_FIELD_BY_TYPE[type]],
    ]),
    ...collectCompanyUrlCandidates(company).filter((value) => isCompanySocialUrl(value)),
  ];

  const links = normalizeRepeatedLinks(candidates, options);
  if (links.length) return links;
  return options.preserveEmpty ? [""] : [];
}

export function buildCompanySocialPayloadFromLinks(links = []) {
  const payload = {
    linkedin_url: "",
    facebook_url: "",
    twitter_url: "",
    google_url: "",
    instagram_url: "",
    youtube_url: "",
    tiktok_url: "",
  };

  for (const rawLink of normalizeRepeatedLinks(links)) {
    const normalizedUrl = normalizeExternalUrl(rawLink);
    if (!normalizedUrl) continue;

    const type = detectCompanySocialType(normalizedUrl, "");
    const field = SOCIAL_FIELD_BY_TYPE[type];
    if (!field || payload[field]) continue;
    payload[field] = normalizedUrl;
  }

  return payload;
}

export function buildCompanyProfileActions(company = {}) {
  const website = getPrimaryCompanyWebsite(company);
  const email =
    safeTrim(company?.contact_email || company?.company_email || company?.email) || "";
  const phone =
    safeTrim(company?.contact_phone || company?.company_phone || company?.phone) || "";
  const socialLinks = getCompanySocialLinksArray(company).map((url) => ({
    type: detectCompanySocialType(url, "website"),
    href: url,
  }));

  return [
    ...(website || !socialLinks.length ? [{ type: "website", href: website }] : []),
    { type: "email", href: email },
    { type: "phone", href: phone },
    ...socialLinks,
  ];
}
